import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import URLBox from './URLBox';
import AddURLModal from './AddURLModal';
import SettingsModal from './SettingsModal';
import WeatherWidget from './WeatherWidget';
import DateWidget from './DateWidget';
import RSSFeed from './RSSFeed';
import './Homepage.css';

function Homepage({ onLogout }) {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingUrl, setEditingUrl] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('homepageSettings');
    return saved ? JSON.parse(saved) : {
      backgroundColor: '#0f0f23',
      cardColor: 'rgba(255, 255, 255, 0.05)',
      textColor: '#ffffff',
      accentColor: '#6366f1',
      backgroundImage: '',
      cardStyle: 'modern',
      weatherLocation: 'New York, NY',
      temperatureUnit: 'F'
    };
  });

  useEffect(() => {
    loadUrls();
    // Refresh metadata after initial load
    setTimeout(() => {
      refreshMetadata();
    }, 2000);
  }, []);

  useEffect(() => {
    localStorage.setItem('homepageSettings', JSON.stringify(settings));
  }, [settings]);

  const loadUrls = async () => {
    try {
      const response = await axios.get('/api/urls', {
        headers: getAuthHeaders()
      });
      setUrls(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading URLs:', error);
      setLoading(false);
    }
  };

  const refreshMetadata = async () => {
    try {
      await axios.post('/api/urls/refresh-metadata', {}, {
        headers: getAuthHeaders()
      });
      loadUrls();
    } catch (error) {
      console.error('Error refreshing metadata:', error);
    }
  };

  const handleAddUrl = async (urlData) => {
    try {
      const response = await axios.post('/api/urls', urlData, {
        headers: getAuthHeaders()
      });
      const newUrl = { ...response.data, order: urls.length };
      setUrls([...urls, newUrl]);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding URL:', error);
      alert('Failed to add URL');
    }
  };

  const handleUpdateUrl = async (id, urlData) => {
    try {
      const response = await axios.put(`/api/urls/${id}`, urlData, {
        headers: getAuthHeaders()
      });
      setUrls(urls.map(u => u.id === id ? response.data : u));
      setEditingUrl(null);
    } catch (error) {
      console.error('Error updating URL:', error);
      alert('Failed to update URL');
    }
  };

  const handleDeleteUrl = async (id) => {
    if (!window.confirm('Are you sure you want to delete this URL?')) {
      return;
    }
    try {
      await axios.delete(`/api/urls/${id}`, {
        headers: getAuthHeaders()
      });
      setUrls(urls.filter(u => u.id !== id));
    } catch (error) {
      console.error('Error deleting URL:', error);
      alert('Failed to delete URL');
    }
  };

  const handleEditUrl = (url) => {
    setEditingUrl(url);
    setShowAddModal(true);
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (id !== draggedId) {
      setDragOverId(id);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const draggedIndex = urls.findIndex(u => u.id === draggedId);
    const targetIndex = urls.findIndex(u => u.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    // Reorder URLs
    const newUrls = [...urls];
    const [removed] = newUrls.splice(draggedIndex, 1);
    newUrls.splice(targetIndex, 0, removed);

    // Update order field
    const updatedUrls = newUrls.map((url, index) => ({
      ...url,
      order: index
    }));

    setUrls(updatedUrls);
    setDraggedId(null);
    setDragOverId(null);

    // Save new order to backend
    try {
      await axios.post('/api/urls/reorder', { urls: updatedUrls.map(u => ({ id: u.id, order: u.order })) }, {
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error('Error saving order:', error);
      // Revert on error
      loadUrls();
    }
  };

  // Use dark theme background, but allow custom background image if set
  const backgroundStyle = settings.backgroundImage ? {
    backgroundImage: `url(${settings.backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  } : {};

  return (
    <div className="homepage" style={backgroundStyle}>
      <header className="homepage-header">
        <div className="header-left">
          <h1>Vero - Homepage</h1>
          <div className="header-widgets">
            <WeatherWidget 
              location={settings.weatherLocation} 
              temperatureUnit={settings.temperatureUnit}
            />
          </div>
        </div>
        <div className="header-actions">
          <DateWidget />
          <button 
            className="icon-button" 
            onClick={() => setShowSettings(true)}
            title="Settings"
          >
            ⚙️
          </button>
          {/* <button 
            className="icon-button" 
            onClick={refreshMetadata}
            title="Refresh Metadata"
          >
            🔄
          </button> */}
        </div>
      </header>

      <main className="homepage-content">
        <div className="homepage-left">
          <RSSFeed />
        </div>
        
        <div className="homepage-right">
          <div className="bookmarks-section">
            <h2 className="bookmarks-title">WEBSITES</h2>
            {loading ? (
              <div className="loading">Loading URLs...</div>
            ) : (
              <>
                <div className="urls-grid">
                  {urls
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map(url => (
                    <URLBox
                      key={url.id}
                      url={url}
                      settings={settings}
                      onEdit={() => handleEditUrl(url)}
                      onDelete={() => handleDeleteUrl(url.id)}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                      onDrop={handleDrop}
                      isDragging={draggedId === url.id}
                      isDragOver={dragOverId === url.id}
                    />
                  ))}
                </div>
                <button 
                  className="add-url-button"
                  onClick={() => {
                    setEditingUrl(null);
                    setShowAddModal(true);
                  }}
                >
                  + Add URL
                </button>
              </>
            )}
          </div>
        </div>
      </main>

      {showAddModal && (
        <AddURLModal
          url={editingUrl}
          onClose={() => {
            setShowAddModal(false);
            setEditingUrl(null);
          }}
          onSave={editingUrl ? 
            (data) => handleUpdateUrl(editingUrl.id, data) : 
            handleAddUrl
          }
        />
      )}

      {showSettings && (
        <SettingsModal
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSave={setSettings}
          onLogout={onLogout}
        />
      )}
    </div>
  );
}

export default Homepage;

